import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoogleLogin } from "@react-oauth/google";
import BrandLogo from "../components/BrandLogo";
import BrandAttribution from "../components/BrandAttribution";
import GoogleRolePicker from "../components/auth/GoogleRolePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { Shield, KeyRound, Lock, Mail } from "lucide-react";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";

const DEMO_ACCOUNTS = [
  { label: "Super Admin", email: "superadmin@factskoot.com" },
  { label: "Organiser", email: "admin@globex.org" },
  { label: "Chair", email: "chair@globex.org" },
  { label: "Delegate", email: "delegate1@globex.org" },
];

/**
 * Demo panel is visible ONLY on preview / localhost.  Hidden on any
 * production domain regardless of `REACT_APP_DEMO_MODE`, so a leaked
 * env-var on the production build can never expose seeded credentials.
 */
function isPreviewLikeHostname() {
  if (typeof window === "undefined") return false;
  const h = (window.location.hostname || "").toLowerCase();
  return (
    h === "localhost"
    || h === "127.0.0.1"
    || h.endsWith(".preview.emergentagent.com")
    || h.endsWith(".emergent.host")
  );
}
const SHOW_DEMO_PANEL =
  process.env.REACT_APP_DEMO_MODE === "true" && isPreviewLikeHostname();

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [pendingGoogle, setPendingGoogle] = useState(null); // {token, profile}
  const {
    login, loginWithGoogle, completeGoogleSignup,
    requestEmailLoginOtp, verifyEmailLoginOtp,
  } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // ── Email-OTP tab state ──
  const [mode, setMode] = useState("password");          // "password" | "otp"
  const [otpStep, setOtpStep] = useState("request");     // "request" | "verify"
  const [otpCode, setOtpCode] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);           // seconds left before resend allowed

  // Tick down resend timer
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const goToTarget = (user) => {
    const from = loc.state?.from;
    const target = from
      ? `${from.pathname || ""}${from.search || ""}${from.hash || ""}`
      : "/dashboard";
    nav(target || "/dashboard", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      goToTarget(res.user);
    } else {
      toast.error(res.error);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleBusy(true);
    try {
      const res = await loginWithGoogle(credentialResponse.credential);
      if (res.ok && res.pending_signup) {
        setPendingGoogle({
          token: res.pending_signup_token,
          profile: res.profile,
        });
        toast.message("One more step — pick your role to finish signing up.");
        return;
      }
      if (res.ok) {
        toast.success(
          res.new_user
            ? `Welcome to Diplomexa, ${res.user.name}!`
            : `Welcome back, ${res.user.name}`
        );
        goToTarget(res.user);
      } else {
        toast.error(res.error || "Google sign-in failed.");
      }
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleGoogleFailure = () => {
    toast.error("Google sign-in could not be completed.");
  };

  const handleRolePickComplete = async (pendingToken, role) => {
    const res = await completeGoogleSignup(pendingToken, role);
    if (res.ok) {
      toast.success(`Welcome to Diplomexa, ${res.user.name}!`);
      goToTarget(res.user);
    }
    return res;
  };

  const quickFill = (e) => {
    setEmail(e);
    setPassword("Admin@12345");
  };

  // ── Email-OTP handlers ──
  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    const targetEmail = (otpEmail || email || "").trim();
    if (!targetEmail) {
      toast.error("Please enter your email first.");
      return;
    }
    setOtpBusy(true);
    const res = await requestEmailLoginOtp(targetEmail);
    setOtpBusy(false);
    if (!res.ok) {
      toast.error(res.error || "Could not send the code. Try again in a moment.");
      return;
    }
    setOtpEmail(targetEmail);
    setOtpStep("verify");
    setResendIn(60);
    if (res.throttled) {
      toast.message("Please wait a minute before requesting another code.");
    } else {
      // Anti-enumeration: we don't know whether the email is actually
      // registered (the API intentionally returns the same 200-OK for
      // unknown emails too).  Phrasing this honestly avoids confusing
      // users while still not leaking which case they hit.
      toast.success("If an account exists with this email, we've sent a sign-in code. Check your inbox and Spam.");
    }
    if (res.mock_otp) {
      // Mock-email environment only — surface the OTP to the developer.
      toast.message(`Dev OTP: ${res.mock_otp}`, { duration: 8000 });
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setOtpBusy(true);
    const res = await verifyEmailLoginOtp(otpEmail, otpCode);
    setOtpBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      goToTarget(res.user);
    } else {
      toast.error(res.error || "Could not verify the code.");
    }
  };

  const switchMode = (next) => {
    setMode(next);
    // Reset transient OTP state when leaving the tab so a re-entry is clean.
    if (next !== "otp") {
      setOtpStep("request");
      setOtpCode("");
      setResendIn(0);
    } else {
      // Pre-fill OTP email from the password-tab email field if present.
      if (!otpEmail && email) setOtpEmail(email);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left decorative */}
      <div className="relative hidden lg:block overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1725618878496-233974f2fd59')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F] via-[#0A192F]/90 to-[#0F4C81]/70" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <BrandLogo light />
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">The Dais</p>
            <h2 className="mt-4 font-serif italic text-4xl leading-tight">
              &ldquo;Order in the house.&rdquo;
            </h2>
            <p className="mt-6 max-w-md text-slate-300">
              Log in to orchestrate your conference with the tools the world&apos;s top MUN
              secretariats rely on.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <Shield className="h-4 w-4 text-[#D4AF37]" />
            <span>Enterprise-grade security &middot; Trusted by leading secretariats</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><BrandLogo /></div>

          {pendingGoogle ? (
            <GoogleRolePicker
              pendingToken={pendingGoogle.token}
              profile={pendingGoogle.profile}
              onComplete={handleRolePickComplete}
              onCancel={() => setPendingGoogle(null)}
            />
          ) : (
            <>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Sign in
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Welcome back, delegate.
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-[#0F4C81] font-semibold hover:underline" data-testid="register-link">
              Create one.
            </Link>
          </p>

          {/* Mode tabs — Password vs Email OTP */}
          <div
            className="mt-8 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1"
            role="tablist"
            aria-label="Sign-in method"
            data-testid="login-mode-tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "password"}
              data-testid="login-tab-password"
              onClick={() => switchMode("password")}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] rounded-md transition-colors ${
                mode === "password"
                  ? "bg-white text-[#0A192F] shadow-sm"
                  : "text-slate-500 hover:text-[#0A192F]"
              }`}
            >
              <Lock className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              Password
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "otp"}
              data-testid="login-tab-otp"
              onClick={() => switchMode("otp")}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] rounded-md transition-colors ${
                mode === "otp"
                  ? "bg-white text-[#0A192F] shadow-sm"
                  : "text-slate-500 hover:text-[#0A192F]"
              }`}
            >
              <Mail className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              Email OTP
            </button>
          </div>

          {mode === "password" ? (
          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-slate-700 tracking-wide">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0F4C81]"
                placeholder="you@conference.org"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold text-slate-700 tracking-wide">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#0F4C81] hover:underline" data-testid="forgot-password-link">
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0F4C81]"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#0F4C81] hover:bg-[#0A3860] text-white font-semibold uppercase tracking-[0.16em] text-sm"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          ) : (
            // ── Email OTP tab ──
            <div className="mt-6 space-y-5" data-testid="login-otp-panel">
              {otpStep === "request" ? (
                <form onSubmit={requestOtp} className="space-y-5" data-testid="otp-request-form">
                  <div>
                    <Label htmlFor="otp-email" className="text-xs font-semibold text-slate-700 tracking-wide">Email</Label>
                    <Input
                      id="otp-email"
                      type="email"
                      required
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      className="mt-2 h-12 bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0F4C81]"
                      placeholder="you@conference.org"
                      data-testid="otp-email-input"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      We&apos;ll email a 6-digit code that expires in 10 minutes.
                    </p>
                  </div>
                  <Button
                    type="submit"
                    disabled={otpBusy}
                    className="w-full h-12 bg-[#0F4C81] hover:bg-[#0A3860] text-white font-semibold uppercase tracking-[0.16em] text-sm"
                    data-testid="otp-request-submit-button"
                  >
                    {otpBusy ? "Sending code…" : "Email me a code"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={submitOtp} className="space-y-5" data-testid="otp-verify-form">
                  <p className="text-xs text-slate-500">
                    If an account exists, a 6-digit code is on its way to{" "}
                    <span className="font-semibold text-[#0A192F]">{otpEmail}</span>.{" "}
                    <button
                      type="button"
                      onClick={() => { setOtpStep("request"); setOtpCode(""); }}
                      className="text-[#0F4C81] font-semibold hover:underline"
                      data-testid="otp-change-email-button"
                    >
                      Change email
                    </button>
                  </p>
                  <div>
                    <Label htmlFor="otp-code" className="text-xs font-semibold text-slate-700 tracking-wide">6-digit code</Label>
                    <Input
                      id="otp-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                      className="mt-2 h-14 text-center font-mono text-2xl tracking-[0.3em] bg-white border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0F4C81]"
                      placeholder="000000"
                      data-testid="otp-code-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={otpBusy || otpCode.length !== 6}
                    className="w-full h-12 bg-[#0F4C81] hover:bg-[#0A3860] text-white font-semibold uppercase tracking-[0.16em] text-sm"
                    data-testid="otp-verify-submit-button"
                  >
                    {otpBusy ? "Verifying…" : "Sign in with OTP"}
                  </Button>
                  <div className="text-center space-y-2">
                    {resendIn > 0 ? (
                      <p className="text-xs text-slate-500" data-testid="otp-resend-timer">
                        Resend a new code in <span className="font-semibold text-[#0A192F]">{resendIn}s</span>
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => requestOtp()}
                        disabled={otpBusy}
                        className="text-xs text-[#0F4C81] font-semibold hover:underline disabled:opacity-50"
                        data-testid="otp-resend-button"
                      >
                        Didn&apos;t get it? Send a new code
                      </button>
                    )}
                    <p className="text-[11px] text-slate-400 leading-relaxed pt-1" data-testid="otp-help-hint">
                      Make sure you&apos;re using the email you registered with.
                      Also check your <span className="font-semibold">Spam</span> /{" "}
                      <span className="font-semibold">Promotions</span> folders — codes can land there.
                    </p>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Google SSO — Factskoot Cloud project */}
          {GOOGLE_CLIENT_ID ? (
            <div className="mt-6" data-testid="google-sso-section">
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
                  <span className="bg-white px-3">or continue with</span>
                </div>
              </div>
              <div
                className="flex justify-center"
                data-testid="google-sso-button-wrapper"
                aria-busy={googleBusy}
              >
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleFailure}
                  theme="outline"
                  size="large"
                  width="320"
                  text="signin_with"
                  shape="rectangular"
                  ux_mode="popup"
                  useOneTap={false}
                />
              </div>
              <p className="text-[11px] text-slate-400 text-center mt-3 flex items-center justify-center gap-1.5">
                <Lock className="h-3 w-3" /> Secured login powered by Google
              </p>
            </div>
          ) : null}

          {SHOW_DEMO_PANEL ? (
            <div className="mt-10 p-5 rounded-lg bg-[#f6f8fb] border border-slate-200" data-testid="login-demo-panel">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="h-3.5 w-3.5 text-[#D4AF37]" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-600 font-bold">
                  Try the demo
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {DEMO_ACCOUNTS.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    onClick={() => quickFill(a.email)}
                    data-testid={`demo-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-left px-3 py-2 rounded-md bg-white border border-slate-200 hover:border-[#D4AF37] transition-colors"
                  >
                    <p className="text-xs font-semibold text-[#0A192F]">{a.label}</p>
                    <p className="text-[11px] text-slate-500 truncate">{a.email}</p>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                All demo accounts share password <span className="font-mono">Admin@12345</span>.
              </p>
            </div>
          ) : null}
            </>
          )}

          {/* Mandatory brand attribution — appears on every public page */}
          <BrandAttribution className="mt-8" />
        </div>
      </div>
    </div>
  );
}
